import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, CheckCircle, Shield, Eye, Lock,
  User, Lightbulb, Target, Zap, BookOpen, MapPin, ClipboardList,
  Trophy, ToggleLeft, ToggleRight, ArrowRight, Plus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── QUESTIONS ────────────────────────────────────────────────────────────────

const STEP0_QUESTIONS = [
  { id: 'age_range', text: 'Quelle est votre tranche d\'âge ?', type: 'choice',
    options: ['15–17 ans', '18–25 ans', '26–35 ans', '36–50 ans', 'Plus de 50 ans'] },
  { id: 'situation', text: 'Quelle est votre situation actuelle ?', type: 'choice',
    options: ['Étudiant(e)', 'En emploi', 'En recherche d\'emploi', 'En reconversion', 'Autre'] },
  { id: 'main_goal', text: 'Quel est votre objectif principal ?', type: 'choice',
    options: ['Trouver un emploi', 'Changer de carrière', 'Développer mes compétences', 'Reprendre des études', 'Autre'] },
  { id: 'digital_comfort', text: 'Comment vous sentez-vous avec le numérique ?', type: 'choice',
    options: ['Débutant(e) — j\'ai besoin d\'aide', 'Je me débrouille pour les bases', 'À l\'aise avec la plupart des outils', 'Expert(e) — très à l\'aise'] },
  { id: 'time_per_week', text: 'Combien de temps par semaine pouvez-vous consacrer à l\'apprentissage ?', type: 'choice',
    options: ['Moins de 2 heures', '2 à 5 heures', '5 à 10 heures', 'Plus de 10 heures'] },
  { id: 'learning_style', text: 'Comment apprenez-vous le mieux ?', type: 'choice',
    options: ['En regardant des vidéos', 'En lisant des textes', 'En pratiquant par des exercices', 'En échangeant avec d\'autres'] },
  { id: 'main_obstacle', text: 'Quel est votre principal frein à l\'apprentissage ?', type: 'choice',
    options: ['Manque de temps', 'Manque de confiance en moi', 'Manque de ressources financières', 'Je ne sais pas par où commencer', 'Autre'] },
];

const STEP1_QUESTIONS = [
  { id: 'identity_words', text: 'En 3 mots, comment vous décririez-vous ?', type: 'text',
    placeholder: 'Ex : curieux, déterminé, créatif...' },
  { id: 'core_values', text: 'Quelles sont vos valeurs les plus importantes ? (choisissez jusqu\'à 3)', type: 'multi',
    max: 3, options: ['Famille', 'Liberté', 'Sécurité', 'Créativité', 'Utilité sociale', 'Reconnaissance', 'Apprentissage', 'Aventure', 'Justice', 'Équilibre vie pro/perso'] },
  { id: 'motivation', text: 'Qu\'est-ce qui vous donne le plus d\'énergie ?', type: 'choice',
    options: ['Aider les autres', 'Créer et innover', 'Résoudre des problèmes', 'Apprendre de nouvelles choses', 'Atteindre des objectifs'] },
  { id: 'strengths', text: 'Quelles qualités vos proches reconnaissent-ils chez vous ? (jusqu\'à 3)', type: 'multi',
    max: 3, options: ['Organisation', 'Communication', 'Empathie', 'Persévérance', 'Créativité', 'Esprit d\'équipe', 'Leadership', 'Rigueur', 'Adaptabilité', 'Curiosité'] },
  { id: 'ideal_environment', text: 'Dans quel environnement vous sentez-vous le plus épanoui(e) ?', type: 'choice',
    options: ['En équipe dans une grande structure', 'En petite équipe / start-up', 'En indépendant(e) / freelance', 'En contact avec le public', 'À distance / télétravail'] },
];

const STEP2_QUESTIONS = [
  { id: 'academic_skills', text: 'Quelles formations ou certifications avez-vous ? (tout ce qui s\'applique)', type: 'multi',
    max: 8, options: ['Baccalauréat', 'BTS / DUT', 'Licence', 'Master / MBA', 'Doctorat', 'Certificats professionnels', 'Formation continue', 'Autodidacte'] },
  { id: 'professional_skills', text: 'Quelles compétences professionnelles maîtrisez-vous ? (jusqu\'à 5)', type: 'multi',
    max: 5, options: ['Gestion de projet', 'Communication / Négociation', 'Analyse de données', 'Vente / Marketing', 'RH / Management', 'Finance / Comptabilité', 'Technique / Informatique', 'Enseignement / Formation', 'Santé / Social', 'Artisanat / Manuel'] },
  { id: 'personal_skills', text: 'Quelles compétences personnelles avez-vous développées ? (jusqu\'à 5)', type: 'multi',
    max: 5, options: ['Langues étrangères', 'Bénévolat / Associatif', 'Sport de compétition', 'Arts / Musique', 'Cuisine / Artisanat', 'Parentalité', 'Voyages / Interculturalité', 'Réseaux sociaux', 'Gestion d\'équipe', 'Autre'] },
  { id: 'digital_levels', text: 'Évaluez vos compétences numériques (0 = débutant, 4 = expert) :', type: 'scale',
    items: [
      { id: 'internet', label: 'Internet et emails' },
      { id: 'office', label: 'Traitement de texte / Tableur' },
      { id: 'social', label: 'Réseaux sociaux pro (LinkedIn)' },
      { id: 'collab', label: 'Outils collaboratifs (Drive, Slack)' },
      { id: 'ai', label: 'Outils d\'intelligence artificielle' },
    ],
    scale_labels: ['Débutant', 'Basique', 'Intermédiaire', 'Avancé', 'Expert'] },
  { id: 'languages', text: 'Quelles langues parlez-vous ?', type: 'multi',
    max: 6, options: ['Français (natif)', 'Anglais', 'Espagnol', 'Arabe', 'Portugais', 'Allemand', 'Chinois', 'Italien', 'Autre'] },
];

const STEP3_QUESTIONS = [
  { id: 'dream_job', text: 'Si tout était possible, quel serait votre emploi idéal ?', type: 'text',
    placeholder: 'Décrivez librement votre idéal professionnel...' },
  { id: 'preferred_sector', text: 'Quel secteur vous attire le plus ? (jusqu\'à 3)', type: 'multi',
    max: 3, options: ['Technologie / Numérique', 'Santé / Social', 'Éducation / Formation', 'Commerce / Vente', 'Art / Culture / Média', 'BTP / Industrie', 'Agriculture / Environnement', 'Finance / Juridique', 'Tourisme / Hôtellerie', 'Sport / Bien-être'] },
  { id: 'geo_constraint', text: 'Quelle est votre contrainte géographique ?', type: 'choice',
    options: ['Je peux me déplacer partout', 'Je préfère rester dans ma région', 'Uniquement à distance / télétravail', 'International possible'] },
  { id: 'timeline', text: 'Dans quel délai visez-vous votre objectif ?', type: 'choice',
    options: ['Immédiatement (moins de 3 mois)', 'Dans les 6 mois', 'Dans l\'année', 'Dans 2–3 ans', 'À long terme (+ de 3 ans)'] },
  { id: 'training_openness', text: 'Êtes-vous prêt(e) à vous former pour atteindre votre objectif ?', type: 'choice',
    options: ['Oui, une formation courte (quelques semaines)', 'Oui, une formation longue (plusieurs mois)', 'Je préfère l\'autodidaxie', 'Je cherche d\'abord à valoriser mes acquis', 'Je ne sais pas encore'] },
];

// ─── COMPUTATION ─────────────────────────────────────────────────────────────

function getBilanName(step0 = {}) {
  const age = step0.age_range || '';
  const situation = step0.situation || '';
  const young = age === '15–17 ans' || age === '18–25 ans';
  const insertion = situation === 'Étudiant(e)' || situation === 'En recherche d\'emploi';
  const adult = age === '26–35 ans' || age === '36–50 ans' || age === 'Plus de 50 ans';
  const working = situation === 'En emploi' || situation === 'En reconversion';
  if (young && insertion) return 'Mon Bilan de départ';
  if (adult && working) return 'Mon Bilan de compétences';
  return 'Mon Profil Kurso';
}

function generateProjectionPaths(responses) {
  const s0 = responses.step_0 || {};
  const s2 = responses.step_2 || {};
  const s3 = responses.step_3 || {};
  const sector = (s3.preferred_sector || [])[0] || 'votre secteur';
  const skills = (s2.professional_skills || []).slice(0, 2).join(' et ') || 'vos compétences';
  return [
    {
      id: 'direct', color: '#20c997', icon: 'zap',
      title: 'Parcours Express',
      subtitle: 'Valoriser vos acquis',
      duration: 'Court terme (1–3 mois)',
      description: `Mettez en avant vos compétences en ${skills} pour accéder rapidement à ${sector}.`,
      steps: ['Mettre à jour votre CV et profil LinkedIn', 'Contacter votre réseau professionnel', 'Candidater à des postes adaptés à votre profil actuel'],
    },
    {
      id: 'formation', color: '#ff6b6b', icon: 'book',
      title: 'Parcours Formation',
      subtitle: 'Développer vos compétences',
      duration: 'Moyen terme (3–12 mois)',
      description: `Suivez une formation ciblée pour vous repositionner dans ${sector}.`,
      steps: ['Identifier une formation courte ou certifiante', 'Commencer les parcours Kurso recommandés', 'Pratiquer avec des projets concrets'],
    },
    {
      id: 'ambitieux', color: '#ffc107', icon: 'target',
      title: 'Parcours Ambitieux',
      subtitle: 'Transformation profonde',
      duration: 'Long terme (1–3 ans)',
      description: `Engagez-vous dans une transformation de votre profil pour atteindre votre idéal : "${s3.dream_job || 'votre objectif'}"`,
      steps: ['Planifier une reconversion ou reprise d\'études', 'Construire un réseau dans votre secteur cible', 'Développer une expertise reconnue'],
    },
  ];
}

function generateActionPlan(responses) {
  const s0 = responses.step_0 || {};
  const s3 = responses.step_3 || {};
  const obstacle = s0.main_obstacle || '';
  const goal = s0.main_goal || '';
  const sector = (s3.preferred_sector || [])[0] || '';
  const actions = [];
  if (obstacle.includes('confiance')) {
    actions.push({ title: 'Renforcer votre confiance', desc: 'Listez 10 réalisations dont vous êtes fier(e) — petites ou grandes.', urgency: 'Cette semaine', color: '#ff6b6b' });
  } else if (obstacle.includes('temps')) {
    actions.push({ title: 'Bloquer du temps d\'apprentissage', desc: 'Réservez 30 min par jour dans votre agenda pour votre projet professionnel.', urgency: 'Aujourd\'hui', color: '#ff6b6b' });
  } else if (obstacle.includes('commencer')) {
    actions.push({ title: 'Faire le premier pas', desc: 'Identifiez une seule action concrète et réalisez-la avant la fin de la semaine.', urgency: 'Cette semaine', color: '#ff6b6b' });
  } else {
    actions.push({ title: 'Clarifier votre objectif', desc: 'Rédigez en 3 phrases précises ce que vous voulez atteindre dans 6 mois.', urgency: 'Aujourd\'hui', color: '#ff6b6b' });
  }
  if (goal.includes('emploi')) {
    actions.push({ title: 'Mettre à jour votre CV', desc: 'Actualisez votre CV avec vos dernières expériences et compétences clés.', urgency: 'Cette semaine', color: '#20c997' });
  } else if (goal.includes('compétences')) {
    actions.push({ title: 'Commencer un cours Kurso', desc: 'Lancez-vous sur un parcours recommandé et complétez le premier module aujourd\'hui.', urgency: 'Aujourd\'hui', color: '#20c997' });
  } else {
    actions.push({ title: 'Explorer les opportunités', desc: 'Recherchez 3 formations ou postes dans votre secteur d\'intérêt.', urgency: 'Cette semaine', color: '#20c997' });
  }
  actions.push({ title: 'Activer votre réseau', desc: 'Contactez 2 personnes dans votre secteur cible pour échanger sur leur parcours.', urgency: 'Ce mois', color: '#ffc107' });

  const GOAL_COURSES = {
    'Trouver un emploi': ['Réussir son entretien d\'embauche', 'Techniques de recherche d\'emploi'],
    'Changer de carrière': ['La reconversion professionnelle réussie', 'Valoriser ses compétences transversales'],
    'Développer mes compétences': ['Méthodes d\'apprentissage efficaces', 'Productivité et organisation'],
    'Reprendre des études': ['Apprendre à apprendre', 'Gestion du temps et des priorités'],
  };
  const SECTOR_COURSES = {
    'Technologie / Numérique': ['Introduction à l\'intelligence artificielle', 'Les bases de la programmation'],
    'Santé / Social': ['Les fondamentaux du travail social', 'Communication bienveillante'],
    'Éducation / Formation': ['Créer et animer des formations', 'Pédagogie et transmission du savoir'],
    'Commerce / Vente': ['Techniques de vente et négociation', 'Marketing digital pour débutants'],
    'Art / Culture / Média': ['Créer du contenu engageant', 'Les fondamentaux du design'],
    'Finance / Juridique': ['Gestion financière personnelle', 'Initiation au droit du travail'],
    'Sport / Bien-être': ['Nutrition et performance', 'Coaching et accompagnement'],
  };
  const recs = [];
  const gr = GOAL_COURSES[goal] || ['Développer son projet professionnel'];
  if (gr[0]) recs.push({ topic: gr[0], reason: 'adapté à votre objectif' });
  if (gr[1]) recs.push({ topic: gr[1], reason: 'complémentaire à votre démarche' });
  const sr = SECTOR_COURSES[sector] || [];
  if (sr[0]) recs.push({ topic: sr[0], reason: `pour votre secteur : ${sector}` });
  return { actions, courseRecs: recs.slice(0, 3) };
}

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

function ProgressBar({ current, total, label }) {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-[#e8e8e8]">{label}</span>
        <span className="text-xs text-[#e8e8e8]">{current}/{total}</span>
      </div>
      <div className="progress-bar-bg h-1.5">
        <div className="progress-bar-fill h-1.5 transition-all duration-500" style={{ width: `${(current / total) * 100}%` }} />
      </div>
    </div>
  );
}

function ChoiceQuestion({ question, value, onChange }) {
  return (
    <div className="space-y-2.5">
      {question.options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`w-full text-left px-4 py-3.5 rounded-2xl border text-sm font-medium transition-all active:scale-[0.98] ${
            value === opt
              ? 'border-[#ff6b6b] bg-[#ff6b6b]/10 text-white'
              : 'border-white/10 text-[#e8e8e8] hover:border-white/20 hover:bg-white/5'
          }`}
          data-testid={`choice-${opt}`}
        >{opt}</button>
      ))}
    </div>
  );
}

function MultiQuestion({ question, value = [], onChange }) {
  const toggle = (opt) => {
    if (value.includes(opt)) {
      onChange(value.filter(v => v !== opt));
    } else if (value.length < (question.max || 99)) {
      onChange([...value, opt]);
    }
  };
  return (
    <div>
      <p className="text-xs text-[#e8e8e8] mb-3">Sélectionnez jusqu'à {question.max}</p>
      <div className="flex flex-wrap gap-2">
        {question.options.map((opt) => {
          const sel = value.includes(opt);
          return (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              className={`px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                sel ? 'border-[#ff6b6b] bg-[#ff6b6b]/10 text-white' : 'border-white/10 text-[#e8e8e8] hover:border-white/20'
              }`}
              data-testid={`multi-${opt}`}
            >{opt}</button>
          );
        })}
      </div>
    </div>
  );
}

function TextQuestion({ question, value = '', onChange }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={question.placeholder}
      rows={3}
      className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#20c997]/50 resize-none transition-colors"
      data-testid="text-question-input"
    />
  );
}

function ScaleQuestion({ question, value = {}, onChange }) {
  const labels = question.scale_labels;
  return (
    <div className="space-y-4">
      {question.items.map((item) => (
        <div key={item.id}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#e8e8e8]">{item.label}</span>
            <span className="text-xs text-[#ff6b6b] font-medium">{labels[value[item.id] || 0]}</span>
          </div>
          <div className="flex gap-2">
            {labels.map((_, i) => (
              <button
                key={i}
                onClick={() => onChange({ ...value, [item.id]: i })}
                className={`flex-1 h-2 rounded-full transition-all ${
                  i <= (value[item.id] || 0) ? 'bg-[#ff6b6b]' : 'bg-white/10'
                }`}
                data-testid={`scale-${item.id}-${i}`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function StepHeader({ icon: Icon, color, title, subtitle }) {
  return (
    <div className="text-center mb-8">
      <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4 border border-white/10"
        style={{ backgroundColor: `${color}15` }}>
        <Icon size={28} style={{ color }} />
      </div>
      <h2 className="font-heading text-2xl font-bold text-white mb-1">{title}</h2>
      <p className="text-[#e8e8e8] text-sm leading-relaxed">{subtitle}</p>
    </div>
  );
}

function IdentityCard({ step0, step1 }) {
  return (
    <div className="space-y-4">
      <div className="kurso-card p-6 border-[#ff6b6b]/20">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-14 h-14 rounded-2xl bg-[#ff6b6b]/10 border border-[#ff6b6b]/20 flex items-center justify-center">
            <User size={24} className="text-[#ff6b6b]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-white text-lg">{step1.identity_words || '—'}</h3>
            <p className="text-[#e8e8e8] text-sm">{step0.situation || ''}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Objectif', value: step0.main_goal, color: '#ff6b6b' },
            { label: 'Motivation', value: step1.motivation, color: '#ffc107' },
            { label: 'Environnement', value: step1.ideal_environment, color: '#20c997' },
            { label: 'Disponibilité', value: step0.time_per_week, color: '#3b82f6' },
          ].map((item) => (
            <div key={item.label} className="bg-[#0f0e17] rounded-xl p-3 border border-white/5">
              <p className="text-xs font-medium mb-1" style={{ color: item.color }}>{item.label}</p>
              <p className="text-white text-xs leading-snug">{item.value || '—'}</p>
            </div>
          ))}
        </div>
        {step1.core_values?.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-[#e8e8e8] mb-2">Mes valeurs</p>
            <div className="flex flex-wrap gap-1.5">
              {step1.core_values.map(v => (
                <span key={v} className="text-xs bg-[#ff6b6b]/10 text-[#ff6b6b] border border-[#ff6b6b]/20 px-2 py-1 rounded-full">{v}</span>
              ))}
            </div>
          </div>
        )}
        {step1.strengths?.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-[#e8e8e8] mb-2">Mes forces</p>
            <div className="flex flex-wrap gap-1.5">
              {step1.strengths.map(s => (
                <span key={s} className="text-xs bg-white/5 text-[#e8e8e8] border border-white/10 px-2 py-1 rounded-full">{s}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SkillsProfile({ step2 }) {
  const digitalAvg = step2.digital_levels
    ? Math.round(Object.values(step2.digital_levels).reduce((a, b) => a + b, 0) / Object.keys(step2.digital_levels).length)
    : 0;
  const SCALE_LABELS = ['Débutant', 'Basique', 'Intermédiaire', 'Avancé', 'Expert'];
  const allSkills = [
    ...(step2.professional_skills || []),
    ...(step2.personal_skills || []),
    ...(step2.academic_skills || []),
  ];
  return (
    <div className="space-y-4">
      {/* Skills tags */}
      <div className="kurso-card p-5 border-[#20c997]/20">
        <h3 className="font-heading font-semibold text-white text-base mb-3 flex items-center gap-2">
          <BookOpen size={16} className="text-[#20c997]" /> Profil de compétences
        </h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {(step2.professional_skills || []).map(s => (
            <span key={s} className="text-xs bg-[#20c997]/10 text-[#20c997] border border-[#20c997]/20 px-2.5 py-1 rounded-full">{s}</span>
          ))}
          {(step2.personal_skills || []).map(s => (
            <span key={s} className="text-xs bg-[#ffc107]/10 text-[#ffc107] border border-[#ffc107]/20 px-2.5 py-1 rounded-full">{s}</span>
          ))}
          {(step2.academic_skills || []).map(s => (
            <span key={s} className="text-xs bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/20 px-2.5 py-1 rounded-full">{s}</span>
          ))}
        </div>
        {/* Digital level */}
        <div className="bg-[#0f0e17] rounded-xl p-3 border border-white/5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-[#e8e8e8]">Niveau numérique global</span>
            <span className="text-xs font-semibold text-[#ff6b6b]">{SCALE_LABELS[Math.min(digitalAvg, 4)]}</span>
          </div>
          <div className="flex gap-1">
            {SCALE_LABELS.map((_, i) => (
              <div key={i} className={`flex-1 h-2 rounded-full ${i <= digitalAvg ? 'bg-[#ff6b6b]' : 'bg-white/10'}`} />
            ))}
          </div>
        </div>
        {step2.languages?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {step2.languages.map(l => (
              <span key={l} className="text-xs bg-[#a855f7]/10 text-[#a855f7] border border-[#a855f7]/20 px-2 py-1 rounded-full">{l}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectionCards({ paths }) {
  const IconMap = { zap: Zap, book: BookOpen, target: Target };
  return (
    <div className="space-y-3">
      {paths.map((path) => {
        const Icon = IconMap[path.icon] || Target;
        return (
          <div key={path.id} className="kurso-card p-5" style={{ borderColor: `${path.color}25` }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${path.color}15` }}>
                <Icon size={16} style={{ color: path.color }} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-white text-base">{path.title}</h3>
                <p className="text-xs" style={{ color: path.color }}>{path.duration}</p>
              </div>
            </div>
            <p className="text-[#e8e8e8] text-sm leading-relaxed mb-3">{path.description}</p>
            <ul className="space-y-1.5">
              {path.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-[#e8e8e8]">
                  <span className="w-4 h-4 rounded-full border shrink-0 mt-0.5 flex items-center justify-center text-[10px] font-bold"
                    style={{ borderColor: path.color, color: path.color }}>{i + 1}</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function ActionPlanView({ plan, onGenerateCourse, generatingCourses = [] }) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-heading font-semibold text-white text-base mb-3 flex items-center gap-2">
          <Target size={16} className="text-[#ff6b6b]" /> 3 actions immédiates
        </h3>
        <div className="space-y-3">
          {plan.actions.map((action, i) => (
            <div key={i} className="kurso-card p-4 flex gap-3">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
                style={{ backgroundColor: `${action.color}15`, color: action.color }}>{i + 1}</span>
              <div>
                <p className="text-white font-medium text-sm">{action.title}</p>
                <p className="text-[#e8e8e8] text-xs mt-0.5 leading-relaxed">{action.desc}</p>
                <span className="text-xs font-semibold mt-1.5 inline-block" style={{ color: action.color }}>{action.urgency}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-heading font-semibold text-white text-base mb-3 flex items-center gap-2">
          <BookOpen size={16} className="text-[#20c997]" /> Parcours Kurso recommandés
        </h3>
        <div className="space-y-2">
          {plan.courseRecs.map((rec, i) => (
            <div key={i} className="kurso-card p-4 flex items-center gap-3">
              <div className="w-9 h-9 bg-[#20c997]/10 rounded-xl flex items-center justify-center shrink-0">
                <BookOpen size={15} className="text-[#20c997]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium leading-snug">{rec.topic}</p>
                <p className="text-[#e8e8e8] text-xs">{rec.reason}</p>
              </div>
              <button
                onClick={() => {
                  const isGen = generatingCourses.includes(rec.topic);
                  if (!isGen) onGenerateCourse(rec.topic);
                }}
                disabled={generatingCourses.includes(rec.topic)}
                className={`shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
                  generatingCourses.includes(rec.topic)
                    ? 'bg-[#20c997]/5 cursor-not-allowed'
                    : 'bg-[#20c997]/10 hover:bg-[#20c997]/20'
                }`}
                data-testid={`add-course-${i}`}
              >
                {generatingCourses.includes(rec.topic)
                  ? <div className="w-3 h-3 border border-[#20c997] border-t-transparent rounded-full animate-spin" />
                  : <Plus size={14} className="text-[#20c997]" />
                }
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#e8e8e8] mt-2">Cliquez sur + pour générer un cours et l'ajouter à vos parcours</p>
      </div>
    </div>
  );
}

function PrivacyModal({ onSave, saving = false, onBack }) {
  const [shareJeSais, setShareJeSais] = useState(false);
  const [shareAction, setShareAction] = useState(false);
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-[#1a1a24] border border-white/10 rounded-3xl p-6 max-w-sm w-full animate-fade-up">
        <div className="flex items-center gap-3 mb-4">
          {onBack && (
            <button onClick={onBack} className="w-8 h-8 bg-[#0f0e17] rounded-xl flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors shrink-0" data-testid="privacy-back-btn">
              <ChevronLeft size={14} className="text-white" />
            </button>
          )}          <div className="w-10 h-10 rounded-2xl bg-[#20c997]/10 flex items-center justify-center">
            <Shield size={20} className="text-[#20c997]" />
          </div>
          <h3 className="font-heading font-bold text-white text-lg">Vos données, votre choix</h3>
        </div>
        <p className="text-[#e8e8e8] text-sm leading-relaxed mb-5">
          Kurso ne transmet jamais vos réponses personnelles sans votre accord. Votre responsable sait que vous avez fait le bilan — pas ce que vous avez répondu, sauf si vous choisissez de partager.
        </p>
        <div className="bg-[#0f0e17] rounded-2xl p-4 border border-white/5 mb-5 space-y-1">
          <p className="text-xs font-semibold text-[#e8e8e8] mb-3">Ce que votre responsable voit toujours :</p>
          {['Votre nom', 'Statut et date du bilan', 'Étapes complétées', 'Cours suivis, XP, série'].map(item => (
            <div key={item} className="flex items-center gap-2 text-xs text-[#e8e8e8]">
              <Eye size={11} className="text-[#ffc107] shrink-0" /> {item}
            </div>
          ))}
        </div>
        <div className="space-y-3 mb-6">
          <p className="text-xs font-semibold text-[#e8e8e8]">Ce que vous pouvez choisir de partager :</p>
          {[
            { label: 'Profil de compétences (Je sais)', value: shareJeSais, set: setShareJeSais, id: 'toggle-je-sais' },
            { label: 'Plan d\'action (Je passe à l\'action)', value: shareAction, set: setShareAction, id: 'toggle-action' },
          ].map(item => (
            <button key={item.label} onClick={() => item.set(!item.value)} data-testid={item.id}
              className="w-full flex items-center justify-between bg-[#0f0e17] rounded-xl p-3 border border-white/5 hover:border-white/10 transition-colors">
              <span className="text-sm text-[#e8e8e8]">{item.label}</span>
              {item.value ? <ToggleRight size={22} className="text-[#20c997]" /> : <ToggleLeft size={22} className="text-[#e8e8e8]" />}
            </button>
          ))}
          <div className="space-y-1">
            {['"Je suis" (identité)', '"Je me projette" (aspirations)'].map(item => (
              <div key={item} className="flex items-center gap-2 text-xs text-[#e8e8e8]">
                <Lock size={11} className="text-[#e8e8e8] shrink-0" /> {item} — toujours privé
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => !saving && onSave(shareJeSais, shareAction)}
          disabled={saving}
          className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-70"
          data-testid="privacy-save-btn"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
              Enregistrement...
            </>
          ) : "Enregistrer mes préférences"}
        </button>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const PHASES = ['privacy', 'step_0', 'step_1', 'result_1', 'step_2', 'result_2', 'step_3', 'result_3', 'step_4', 'privacy_modal', 'done'];
const STEP_QUESTIONS = { step_0: STEP0_QUESTIONS, step_1: STEP1_QUESTIONS, step_2: STEP2_QUESTIONS, step_3: STEP3_QUESTIONS };

export default function BilanPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('privacy');
  const [qIdx, setQIdx] = useState(0);
  const [responses, setResponses] = useState({ step_0: {}, step_1: {}, step_2: {}, step_3: {}, step_4: {} });
  const [computed, setComputed] = useState({});
  const [saving, setSaving] = useState(false);
  const [generatingCourses, setGeneratingCourses] = useState([]);
  const [completion, setCompletion] = useState(null);

  // Load existing bilan on mount
  useEffect(() => {
    axios.get(`${API}/bilan`, { withCredentials: true })
      .then(({ data }) => {
        if (data.exists !== false && data.responses) {
          setResponses(prev => ({ ...prev, ...Object.fromEntries(
            Object.entries(data.responses).map(([k, v]) => [k.replace('step_', 'step_'), v])
          )}));
          if (data.completed) {
            setPhase('done');
          }
        }
      })
      .catch(() => {});
  }, []);

  const bilanName = getBilanName(responses.step_0);
  const currentStepKey = phase.startsWith('step_') ? phase : null;
  const currentQuestions = currentStepKey ? STEP_QUESTIONS[currentStepKey] || [] : [];
  const currentQ = currentQuestions[qIdx];
  const currentVal = currentStepKey ? (responses[currentStepKey]?.[currentQ?.id] ?? (currentQ?.type === 'multi' ? [] : currentQ?.type === 'scale' ? {} : '')) : null;

  const setVal = (val) => {
    if (!currentStepKey || !currentQ) return;
    setResponses(prev => ({
      ...prev,
      [currentStepKey]: { ...prev[currentStepKey], [currentQ.id]: val },
    }));
  };

  const canAdvance = () => {
    if (!currentQ) return true;
    if (currentQ.type === 'text') return (currentVal || '').trim().length > 0;
    if (currentQ.type === 'multi') return (currentVal || []).length > 0;
    if (currentQ.type === 'scale') return true;
    return !!currentVal;
  };

  const saveStep = async (stepKey) => {
    const stepNum = parseInt(stepKey.replace('step_', ''));
    try {
      await axios.post(`${API}/bilan/step`, { step: stepNum, responses: responses[stepKey] }, { withCredentials: true });
    } catch (e) { console.error(e); }
  };

  const advanceQuestion = async () => {
    if (qIdx < currentQuestions.length - 1) {
      setQIdx(qIdx + 1);
    } else {
      // End of this step
      setSaving(true);
      await saveStep(currentStepKey);
      setSaving(false);
      // Move to result or next step
      if (currentStepKey === 'step_0') {
        setQIdx(0); setPhase('step_1');
      } else if (currentStepKey === 'step_1') {
        setPhase('result_1'); setQIdx(0);
      } else if (currentStepKey === 'step_2') {
        setPhase('result_2'); setQIdx(0);
      } else if (currentStepKey === 'step_3') {
        const paths = generateProjectionPaths(responses);
        setComputed(prev => ({ ...prev, paths }));
        setPhase('result_3'); setQIdx(0);
      } else if (currentStepKey === 'step_4') {
        setPhase('privacy_modal');
      }
    }
  };

  const handleGenerateCourse = async (topic) => {
    setGeneratingCourses(prev => [...prev, topic]);
    try {
      await axios.post(`${API}/courses/generate`, { topic }, { withCredentials: true });
      toast.success(`Cours "${topic.substring(0, 35)}" lancé — retrouvez-le sur votre tableau de bord`);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 429) {
        toast.error("Limite de génération atteinte. Passez à un plan supérieur.", {
          action: { label: "Mettre à niveau", onClick: () => navigate('/pricing') }
        });
      } else {
        toast.error("Erreur lors de la génération — réessayez");
      }
    }
    setGeneratingCourses(prev => prev.filter(t => t !== topic));
  };

  const handleComplete = async (shareJeSais, shareAction) => {
    setSaving(true);
    try {
      const plan = generateActionPlan(responses);
      await saveStep('step_4');
      await axios.put(`${API}/bilan/privacy`, { share_je_sais: shareJeSais, share_action_plan: shareAction }, { withCredentials: true });
      const { data } = await axios.post(`${API}/bilan/complete`, {}, { withCredentials: true });
      setCompletion(data);
      await refreshUser();
      toast.success("✓ Vos préférences ont été enregistrées");
      setPhase('done');
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement — réessayez");
      console.error(e);
    }
    setSaving(false);
  };

  const stepNumbers = { step_0: 0, step_1: 1, result_1: 1, step_2: 2, result_2: 2, step_3: 3, result_3: 3, step_4: 4, privacy_modal: 4, done: 5 };
  const currentStepNum = stepNumbers[phase] || 0;

  // ── Privacy notice ──
  if (phase === 'privacy') {
    return (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4 py-10">
        <div className="max-w-sm w-full space-y-5 animate-fade-up">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-[#e8e8e8] text-sm hover:text-white transition-colors">
            <ChevronLeft size={16} /> Retour
          </button>
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#20c997]/10 border border-[#20c997]/20 flex items-center justify-center mx-auto mb-4">
              <ClipboardList size={28} className="text-[#20c997]" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">{bilanName}</h1>
            <p className="text-[#e8e8e8] text-sm leading-relaxed">
              Un bilan en 4 étapes pour mieux vous connaître, identifier vos compétences et tracer votre chemin.
            </p>
          </div>
          <div className="kurso-card p-5 border-[#20c997]/20">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={15} className="text-[#20c997]" />
              <span className="text-sm font-semibold text-white">Vos données vous appartiennent</span>
            </div>
            <p className="text-[#e8e8e8] text-sm leading-relaxed">
              Kurso ne transmet jamais vos réponses personnelles sans votre accord. Votre responsable sait que vous avez fait le bilan — pas ce que vous avez répondu, sauf si vous choisissez de partager.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: User, label: 'Je suis', desc: 'Identité & motivations', color: '#ff6b6b' },
              { icon: BookOpen, label: 'Je sais', desc: 'Compétences & acquis', color: '#20c997' },
              { icon: MapPin, label: 'Je me projette', desc: '3 parcours possibles', color: '#ffc107' },
              { icon: Zap, label: 'Je passe à l\'action', desc: '3 actions + cours', color: '#a855f7' },
            ].map(step => (
              <div key={step.label} className="kurso-card p-3">
                <step.icon size={16} className="mb-1.5" style={{ color: step.color }} />
                <p className="text-white text-sm font-semibold">{step.label}</p>
                <p className="text-[#e8e8e8] text-xs">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center text-[#e8e8e8] text-xs">
            Durée estimée : 10–15 minutes · +200 XP à la fin
          </div>
          <button onClick={() => setPhase('step_0')} className="btn-primary w-full py-4 text-base" data-testid="start-bilan-btn">
            Commencer mon bilan <ArrowRight size={18} className="inline ml-2" />
          </button>
        </div>
      </div>
    );
  }

  // ── Active question step ──
  if (currentStepKey && currentQ) {
    const stepMeta = {
      step_0: { title: 'Point de départ', color: '#ffc107', icon: Zap, total: 7, label: 'Évaluation initiale' },
      step_1: { title: 'Je suis', color: '#ff6b6b', icon: User, total: 5, label: 'Étape 1 / 4' },
      step_2: { title: 'Je sais', color: '#20c997', icon: BookOpen, total: 5, label: 'Étape 2 / 4' },
      step_3: { title: 'Je me projette', color: '#ffc107', icon: MapPin, total: 5, label: 'Étape 3 / 4' },
      step_4: { title: 'Je passe à l\'action', color: '#a855f7', icon: Zap, total: 0, label: 'Étape 4 / 4' },
    };
    const meta = stepMeta[currentStepKey];

    return (
      <div className="min-h-screen bg-[#0f0e17] px-4 py-6">
        <div className="max-w-sm mx-auto">
          <div className="flex items-center justify-between mb-6">
            {!(currentStepKey === 'step_0' && qIdx === 0) ? (
              <button
                onClick={() => {
                  if (qIdx > 0) { setQIdx(qIdx - 1); return; }
                  if (currentStepKey === 'step_1') { setPhase('step_0'); setQIdx(STEP0_QUESTIONS.length - 1); }
                  else if (currentStepKey === 'step_2') { setPhase('result_1'); }
                  else if (currentStepKey === 'step_3') { setPhase('result_2'); }
                  else setPhase('privacy');
                }}
                className="w-9 h-9 bg-[#1a1a24] rounded-xl flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
                data-testid="bilan-back-btn"
              >
                <ChevronLeft size={16} className="text-white" />
              </button>
            ) : (
              <div className="w-9 h-9" />
            )}
            <span className="text-xs font-semibold" style={{ color: meta.color }}>{meta.label}</span>
            <span className="text-xs text-[#e8e8e8]">{qIdx + 1}/{currentQuestions.length}</span>
          </div>
          <ProgressBar current={qIdx + 1} total={currentQuestions.length} label={meta.title} />
          <div className="mb-8 animate-fade-up" key={`${currentStepKey}-${qIdx}`}>
            <h2 className="font-heading text-xl font-bold text-white mb-6 leading-snug">{currentQ.text}</h2>
            {currentQ.type === 'choice' && <ChoiceQuestion question={currentQ} value={currentVal} onChange={setVal} />}
            {currentQ.type === 'multi' && <MultiQuestion question={currentQ} value={currentVal} onChange={setVal} />}
            {currentQ.type === 'text' && <TextQuestion question={currentQ} value={currentVal} onChange={setVal} />}
            {currentQ.type === 'scale' && <ScaleQuestion question={currentQ} value={currentVal} onChange={setVal} />}
          </div>
          <button
            onClick={advanceQuestion}
            disabled={!canAdvance() || saving}
            className="btn-primary w-full py-4 text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={canAdvance() ? { backgroundColor: meta.color } : {}}
            data-testid="bilan-next-btn"
          >
            {saving ? <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" /> : (
              <>
                {qIdx < currentQuestions.length - 1 ? 'Suivant' : 'Valider'} <ChevronRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── Result screens ──
  if (phase === 'result_1') {
    return (
      <div className="min-h-screen bg-[#0f0e17] px-4 py-6">
        <div className="max-w-sm mx-auto space-y-5 animate-fade-up">
          <button onClick={() => { setPhase('step_1'); setQIdx(STEP1_QUESTIONS.length - 1); }} className="w-9 h-9 bg-[#1a1a24] rounded-xl flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors" data-testid="result1-back-btn">
            <ChevronLeft size={16} className="text-white" />
          </button>
          <div className="text-center">
            <CheckCircle size={40} className="text-[#ff6b6b] mx-auto mb-3" />
            <h2 className="font-heading text-2xl font-bold text-white">Votre carte identité</h2>
            <p className="text-[#e8e8e8] text-sm mt-1">Étape 1 / 4 complétée</p>
          </div>
          <IdentityCard step0={responses.step_0} step1={responses.step_1} />
          <button onClick={() => { setQIdx(0); setPhase('step_2'); }} className="btn-primary w-full py-4 text-sm" data-testid="result-next-btn">
            Étape suivante : Je sais <ChevronRight size={16} className="inline ml-1" />
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'result_2') {
    return (
      <div className="min-h-screen bg-[#0f0e17] px-4 py-6">
        <div className="max-w-sm mx-auto space-y-5 animate-fade-up">
          <button onClick={() => { setPhase('step_2'); setQIdx(STEP2_QUESTIONS.length - 1); }} className="w-9 h-9 bg-[#1a1a24] rounded-xl flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors" data-testid="result2-back-btn">
            <ChevronLeft size={16} className="text-white" />
          </button>
          <div className="text-center">
            <CheckCircle size={40} className="text-[#20c997] mx-auto mb-3" />
            <h2 className="font-heading text-2xl font-bold text-white">Votre profil de compétences</h2>
            <p className="text-[#e8e8e8] text-sm mt-1">Étape 2 / 4 complétée</p>
          </div>
          <SkillsProfile step2={responses.step_2} />
          <button onClick={() => { setQIdx(0); setPhase('step_3'); }} className="btn-primary w-full py-4 text-sm" style={{ backgroundColor: '#20c997' }} data-testid="result-next-btn">
            Étape suivante : Je me projette <ChevronRight size={16} className="inline ml-1" />
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'result_3') {
    const paths = computed.paths || generateProjectionPaths(responses);
    return (
      <div className="min-h-screen bg-[#0f0e17] px-4 py-6">
        <div className="max-w-sm mx-auto space-y-5 animate-fade-up">
          <button onClick={() => { setPhase('step_3'); setQIdx(STEP3_QUESTIONS.length - 1); }} className="w-9 h-9 bg-[#1a1a24] rounded-xl flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors" data-testid="result3-back-btn">
            <ChevronLeft size={16} className="text-white" />
          </button>
          <div className="text-center">
            <CheckCircle size={40} className="text-[#ffc107] mx-auto mb-3" />
            <h2 className="font-heading text-2xl font-bold text-white">Vos 3 pistes réalistes</h2>
            <p className="text-[#e8e8e8] text-sm mt-1">Étape 3 / 4 complétée</p>
          </div>
          <ProjectionCards paths={paths} />
          <button
            onClick={() => {
              const plan = generateActionPlan(responses);
              setComputed(prev => ({ ...prev, plan }));
              setResponses(prev => ({ ...prev, step_4: { action_plan: plan } }));
              setPhase('step_4');
            }}
            className="btn-primary w-full py-4 text-sm" style={{ backgroundColor: '#ffc107', color: '#0f0e17' }}
            data-testid="result-next-btn"
          >
            Étape suivante : Je passe à l'action <ChevronRight size={16} className="inline ml-1" />
          </button>
        </div>
      </div>
    );
  }

  // ── Step 4 action plan (special — no question loop) ──
  if (phase === 'step_4') {
    const plan = computed.plan || generateActionPlan(responses);
    return (
      <div className="min-h-screen bg-[#0f0e17] px-4 py-6 pb-10">
        <div className="max-w-sm mx-auto space-y-5 animate-fade-up">
          <button onClick={() => setPhase('result_3')} className="w-9 h-9 bg-[#1a1a24] rounded-xl flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors" data-testid="step4-back-btn">
            <ChevronLeft size={16} className="text-white" />
          </button>
          <StepHeader icon={Zap} color="#a855f7" title="Je passe à l'action" subtitle="Étape 4 / 4 — Vos actions immédiates et parcours recommandés" />
          <ActionPlanView
            plan={plan}
            onGenerateCourse={(topic) => handleGenerateCourse(topic)}
            generatingCourses={generatingCourses}
          />
          <button onClick={() => setPhase('privacy_modal')} className="btn-primary w-full py-4 text-sm mt-4" style={{ backgroundColor: '#a855f7' }} data-testid="finalize-bilan-btn">
            Finaliser mon bilan <ArrowRight size={16} className="inline ml-1" />
          </button>
        </div>
      </div>
    );
  }

  // ── Privacy modal ──
  if (phase === 'privacy_modal') {
    return <PrivacyModal onSave={handleComplete} saving={saving} onBack={() => setPhase('step_4')} />;
  }

  // ── Done ──
  if (phase === 'done') {
    return (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-sm w-full text-center space-y-6 animate-fade-up">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#20c997]/20 to-[#ffc107]/20 border border-[#20c997]/30 flex items-center justify-center animate-pulse-glow">
            <Trophy size={44} className="text-[#ffc107]" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Bilan complété !</h1>
            <p className="text-[#e8e8e8] text-base">{bilanName}</p>
          </div>
          {(completion?.xp_gained > 0 || completion?.already_completed !== true) && (
            <div className="bg-[#ffc107]/10 border border-[#ffc107]/20 rounded-2xl p-5">
              <div className="font-heading text-3xl font-bold text-[#ffc107] mb-1">+200 XP</div>
              <p className="text-[#e8e8e8] text-sm">Bilan Kurso complété</p>
              {completion?.new_badges?.length > 0 && (
                <div className="mt-3">
                  <span className="teal-badge inline-flex items-center gap-1">
                    <CheckCircle size={12} /> Badge débloqué : Bilan complété
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="space-y-3">
            <button onClick={() => navigate('/dashboard')} className="btn-primary w-full py-4 text-base" data-testid="bilan-done-dashboard-btn">
              Retour au tableau de bord <ArrowRight size={18} className="inline ml-2" />
            </button>
            <button onClick={() => navigate('/profile')} className="btn-secondary w-full py-3.5 text-sm" data-testid="bilan-done-profile-btn">
              Voir mon profil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
